import { Check } from "@phosphor-icons/react";
import { Button, Dialog, Flex, TextField, Text, Grid } from "@radix-ui/themes";
import { useSearch } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";

import { useAppSelector } from "../../../utils/hooks";

type RenameDialogProps = {
  _id?: string;
  onSave(ticketId: string, name: string): Promise<any> | void;
  onCancel(): void;
  isPending: boolean;
  name?: string;
};

export function RenameDialog(props: RenameDialogProps) {
  const { isPending, onCancel, onSave, _id } = props;
  const tickets = useAppSelector((state) => state.punttProjects.tickets);
  const folders = useAppSelector((state) => state.punttProjects.folders);
  const breadcrumbs = useAppSelector(
    (state) => state.punttProjects.breadcrumbs,
  );

  const { folderId } = useSearch({
    strict: false,
  });
  const markedFolders = useMemo(
    () =>
      folders.map((f) => ({
        ...f,
        isFolder: true,
      })),
    [folders],
  );
  const mergedFoldersAndTickets = useMemo(
    () =>
      [...markedFolders, ...tickets].toSorted((a, b) => {
        const dateA = new Date(a.updatedAt as string);
        const dateB = new Date(b.updatedAt as string);

        // Sort in descending order (most recent first)
        return dateB.getTime() - dateA.getTime();
      }),
    [markedFolders, tickets],
  );

  const found =
    mergedFoldersAndTickets.find((t) => t._id === _id) ??
    breadcrumbs.find((b) => b._id === _id);

  const isFolder = found && "isFolder" in found && found?.isFolder;
  const [name, setName] = useState(
    // @ts-expect-error TS2339: Yeah, this type is really messed up.
    found?.name ?? found?.title ?? props.name ?? "",
  );

  useEffect(() => {
    // @ts-expect-error TS2339: Yeah, this type is really messed up.
    setName(found?.name ?? found?.title ?? props.name ?? "");
  }, [found?._id, props.name]);

  return (
    <Dialog.Root
      open={_id != null}
      onOpenChange={(open) => !open && onCancel()}
    >
      <Dialog.Content maxWidth="450px">
        <Dialog.Title>
          Rename {isFolder || _id === folderId ? "folder" : "ticket"}?
        </Dialog.Title>
        <Dialog.Description className="hidden">
          Rename <em>{name}</em>
        </Dialog.Description>

        <Grid gap="2">
          <Text size="2" weight="medium">
            {isFolder || _id === folderId ? "Folder" : "Ticket"} Name
          </Text>
          <TextField.Root
            placeholder={`${isFolder || _id === folderId ? "Folder" : "Ticket"} Name`}
            name="folder-name"
            onChange={(e) => setName(e.target.value)}
            value={name}
            disabled={isPending}
            onKeyDown={async (e) => {
              if (e.key === "Enter" && !isPending) {
                e.preventDefault();
                await onSave(_id as string, name);
                setName("");
              }
            }}
          />
        </Grid>

        <Flex gap="2" mt="3" justify="end">
          <Button
            variant="soft"
            color="gray"
            onClick={() => {
              onCancel();
              setName("");
            }}
          >
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={async () => {
              await onSave(_id as string, name);
              setName("");
            }}
            disabled={isPending || !name.trim().length}
            loading={isPending}
          >
            <Check />
            Rename
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
